// Catalogue translation helper — module-level so it works inside any callback
function _ct(key, fallback) {
    return (window.catalogueTranslations && window.catalogueTranslations[key]) || fallback;
}

import CartController from '../cart/CartController';
import fragrance from "../../unique_fragrances.json";

/**
 * CatalogueController
 *
 * Changes from original:
 *
 *  BOTTLE REMOVED
 *  - _openPanel(): threeController.addPerfumeBottle() calls removed
 *  - All canvas.webgl clip-path manipulation removed
 *  - mobile-bottle body class removed
 *
 *  DESKTOP HERO  (_openDesktopPanel)
 *  - cdpHeroBg now shows the owner's crafting photo as a cover image
 *  - A cream label card is injected centred in the hero (50%/50%)
 *    so the perfume name appears to be "stamped" on the bottle in the photo
 *  - Photo path: ./assets/owner-crafting.jpg  ← drop the real file here
 *
 *  MOBILE HERO  (_openMobileSheet)
 *  - cssHeroBg keeps the existing _colourForItem() gradient
 *  - Floating note-tag pills are injected into cssHero
 *  - One-time keyframe style block injected on first open
 *
 *  FETCH CACHE  (showPerfumeDetails)
 *  - get_prices.php?category=Perfumes is cached in this._priceCache
 *    so repeat clicks on different perfumes cost zero extra network requests
 */
export default class CatalogueController {

    constructor(threeController) {
        this.perfumesListElement = document.getElementById('perfumesList');
        this.cartController      = new CartController();
        this.threeController     = threeController;

        this.currentPage  = 1;
        this.itemsPerPage = 12;
        this.allProducts  = [];
        this.allPerfumes  = [];
        this.activeType   = null;

        // ── Fetch caches ──────────────────────────────────────────────────
        this._priceCache   = null;       // perfume prices (shared across all perfumes)
        this._perfumeCache = new Map();  // perfumes by gender key — avoids repeat DB hits

        this.init();
    }

    // Instance wrapper — delegates to module-level _ct()
    _t(key, fallback) { return _ct(key, fallback); }

    // ── Pre-fetch min perfume price for card display ──────────────────────
    _preloadMinPrice() {
        if (this._minPerfumePrice) return;
        var self = this;
        fetch('./get_prices.php?category=Perfumes')
            .then(function(r) { return r.json(); })
            .then(function(prices) {
                if (prices && prices.length) {
                    self._minPerfumePrice = Math.min.apply(null, prices.map(function(p) { return Number(p.price); }));
                }
            })
            .catch(function() {});
    }

    // ── Mobile filter overlay click → close sidebar ───────────────────────
    _initSidebarOverlay() {
        var overlay = document.getElementById('catFilterOverlay');
        var self = this;
        if (overlay) {
            overlay.addEventListener('click', function() {
                var sidebar = document.getElementById('catSidebar');
                if (sidebar) sidebar.classList.add('collapsed');
                overlay.classList.remove('open');
            });
        }
    }

    // ── Collapse sidebar (called on category pick) ────────────────────────
    _collapseSidebar() {
        var sidebar = document.getElementById('catSidebar');
        var overlay = document.getElementById('catFilterOverlay');
        if (sidebar) sidebar.classList.add('collapsed');
        if (overlay) overlay.classList.remove('open');
    }

    // ── Toggle sidebar open/closed (Categories button) ────────────────────
    _toggleSidebar() {
        var sidebar = document.getElementById('catSidebar');
        var overlay = document.getElementById('catFilterOverlay');
        if (!sidebar) return;
        var isCollapsed = sidebar.classList.contains('collapsed');
        sidebar.classList.toggle('collapsed');
        if (overlay && window.innerWidth <= 1023) {
            if (isCollapsed) { overlay.classList.add('open'); }
            else             { overlay.classList.remove('open'); }
        }
    }

    init() {
        this._initPaginationContainer();
        this._initSidebar();
        this.initBackToTopButton();
        this._preloadMinPrice();
        this._initSidebarOverlay();
    }

    // ── Sidebar ───────────────────────────────────────────────────────────

    _initSidebar() {
        document.querySelectorAll('.csb-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.csb-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this._closeDetailPanel();

                const gender   = btn.dataset.gender;
                const category = btn.dataset.category;
                const type     = btn.dataset.type;

                if (type === 'perfumes' && gender) {
                    this.filterPerfumesByGender(gender);
                } else if (category) {
                    this.filterCategories(category);
                }

                document.getElementById('catListEmpty').style.display   = 'none';
                document.getElementById('catListContent').style.display = 'flex';
                this._collapseSidebar();
            });
        });
    }

    // ── Pagination ────────────────────────────────────────────────────────

    _initPaginationContainer() {
        this.paginationElement = document.createElement('div');
        this.paginationElement.id = 'cataloguePagination';
        this.paginationElement.classList.add('catalogue-pagination');
        const footer = document.getElementById('clcFooter');
        if (footer) footer.appendChild(this.paginationElement);
        else this.perfumesListElement.insertAdjacentElement('afterend', this.paginationElement);
    }

    _renderPaginationBar(total) {
        this.paginationElement.innerHTML = '';
        const totalPages = Math.ceil(total / this.itemsPerPage);
        if (totalPages <= 1) return;

        const prevBtn = document.createElement('button');
        prevBtn.classList.add('pagination-status-nav');
        prevBtn.innerHTML = '&#8249; ' + _ct('prev', 'Prev');
        if (this.currentPage === 1) prevBtn.classList.add('disabled');
        prevBtn.addEventListener('click', () => this._goToPage(this.currentPage - 1));

        const center = document.createElement('div');
        center.classList.add('pagination-status-center');
        const pct = Math.round((this.currentPage / totalPages) * 100);
        center.innerHTML = `
            <div class="pagination-status-current">${this.currentPage}</div>
            <div class="pagination-status-total">${_ct("of", "of")} ${totalPages} ${_ct("pages", "pages")}</div>
            <div class="pagination-status-track">
                <div class="pagination-status-fill" style="width:${pct}%"></div>
            </div>`;

        const nextBtn = document.createElement('button');
        nextBtn.classList.add('pagination-status-nav');
        nextBtn.innerHTML = _ct('next', 'Next') + ' &#8250;';
        if (this.currentPage === totalPages) nextBtn.classList.add('disabled');
        nextBtn.addEventListener('click', () => this._goToPage(this.currentPage + 1));

        this.paginationElement.appendChild(prevBtn);
        this.paginationElement.appendChild(center);
        this.paginationElement.appendChild(nextBtn);
    }

    _goToPage(page) {
        const array = this.activeType === 'perfumes' ? this.allPerfumes : this.allProducts;
        const total = Math.ceil(array.length / this.itemsPerPage);
        if (page < 1 || page > total) return;
        this.currentPage = page;
        this._renderPage();
        this._closeDetailPanel();
        const body = document.querySelector('.clc-body');
        if (body) body.scrollTop = 0;
    }

    _renderPage() {
        const array     = this.activeType === 'perfumes' ? this.allPerfumes : this.allProducts;
        const start     = (this.currentPage - 1) * this.itemsPerPage;
        const pageItems = array.slice(start, start + this.itemsPerPage);

        this.perfumesListElement.innerHTML = '';
        const frag = document.createDocumentFragment();
        pageItems.forEach((item, i) => {
            const el = this.activeType === 'perfumes'
                ? this.createPerfumeElement(item, start + i)
                : this.createProductElement(item, start + i);
            frag.appendChild(el);
        });
        this.perfumesListElement.appendChild(frag);
        this._renderPaginationBar(array.length);
        this.perfumesListElement.classList.add('cat-card-grid');
    }

    // ── Fetching ──────────────────────────────────────────────────────────

    filterPerfumesByGender(gender) {
        // ── Cache hit — instant render, zero network ──────────────────────
        if (this._perfumeCache.has(gender)) {
            this.displayPerfumes(this._perfumeCache.get(gender), gender);
            return;
        }

        // ── Cache miss — show skeleton immediately, fetch in background ───
        this._showSkeletonCards();

        // Auto-collapse sidebar on portrait tablet so detail panel has full width
        if (window.innerWidth <= 1023 && window.innerWidth > 768) {
            var _ts = document.getElementById('catSidebar');
            var _to = document.getElementById('catFilterOverlay');
            if (_ts) _ts.classList.add('collapsed');
            if (_to) _to.classList.remove('open');
        }

        let endpoint = './get_perfumes.php';
        if (gender === 'men')    endpoint += '?gender=men';
        if (gender === 'women')  endpoint += '?gender=women';
        if (gender === 'unisex') endpoint += '?gender=unisex';

        fetch(endpoint)
            .then(r => r.json())
            .then(data => {
                this._perfumeCache.set(gender, data); // cache for next time
                this.displayPerfumes(data, gender);
            })
            .catch(err => console.error('Error fetching perfumes:', err));
    }

    // ── Skeleton: 12 placeholder cards matching real card dimensions ──────
    _showSkeletonCards() {
        this.perfumesListElement.innerHTML = '';
        this.perfumesListElement.classList.add('cat-card-grid');
        this.paginationElement.innerHTML = '';
        const frag = document.createDocumentFragment();
        for (let i = 0; i < 12; i++) {
            const card = document.createElement('div');
            card.className = 'cat-skeleton-card';
            card.innerHTML =
                '<div class="cat-skeleton-hero"></div>' +
                '<div class="cat-skeleton-body">' +
                    '<div class="cat-skeleton-line cat-skeleton-name"></div>' +
                    '<div class="cat-skeleton-line cat-skeleton-sub"></div>' +
                    '<div class="cat-skeleton-line cat-skeleton-price"></div>' +
                '</div>';
            frag.appendChild(card);
        }
        this.perfumesListElement.appendChild(frag);
    }

    filterCategories(categoryKey) {
        const categoryMap = {
            candles:     'Aromatic Candles',
            cstBody:     'Customised Body Care Products',
            oliBody:     'Olivaloe Body Care Products',
            carrier:     'Carrier Oils',
            essential:   'Essential Oils',
            autFreshers: 'Auto Freshers',
            intFreshers: 'Interior Freshers',
            moistSkcare: 'Moisturizer Skin Creams',
            colognes:    'Colognes',
            soapanti:    'Soaps'
        };
        const categoryName = categoryMap[categoryKey];

        // Translated display label for the clc-title header
        const labelMap = {
            candles:     _ct('aromatic_candles',  'Aromatic Candles'),
            cstBody:     _ct('customized_body',   'Customised Body Care Products'),
            oliBody:     _ct('olivaloe_body',     'Olivaloe Body Care Products'),
            carrier:     _ct('carrier_oils',      'Carrier Oils'),
            essential:   _ct('essential_oils',    'Essential Oils'),
            autFreshers: _ct('auto_freshers',     'Auto Freshers'),
            intFreshers: _ct('interior_freshers', 'Interior Freshers'),
            moistSkcare: _ct('moisturizer_skin',  'Moisturizer Skin Creams'),
            colognes:    _ct('colognes',          'Colognes'),
            soapanti:    _ct('soaps_antiseptics', 'Soaps'),
        };
        const categoryLabel = labelMap[categoryKey] || categoryName;


        fetch('./get_perfumes.php')
            .then(r => r.json())
            .then(d => { this.perfumesData = d; })
            .catch(() => {});

        fetch('./get_products.php?category=' + categoryName)
            .then(r => { if (!r.ok) throw new Error('Network error'); return r.json(); })
            .then(data => {
                if (!data.length) {
                    this.perfumesListElement.innerHTML = '<p>No products found.</p>';
                    this.paginationElement.innerHTML   = '';
                    return;
                }
                const products = data.map(p => ({
                    category:       categoryName,
                    productname:    p.productname,
                    image:          p.image,
                    price:          p.price,
                    weight:         p.weight,
                    productid:      p.productid,
                    description:    p.description    || '',
                    description_gr: p.description_gr || '',
                    description_en: p.description_en || ''
                }));
                this.displayProducts(products, categoryLabel);
            })
            .catch(err => {
                console.error('Error fetching products:', err);
                this.perfumesListElement.innerHTML = '<p>Error loading products.</p>';
            });
    }

    // ── Display ───────────────────────────────────────────────────────────

    displayProducts(products, label) {
        products = Array.isArray(products) ? products : [products];
        this.allProducts = products;
        this.activeType  = 'products';
        this.currentPage = 1;
        this._updateListHeader(label || products[0]?.category || _ct('options', 'Products'), products.length);
        this._renderPage();
    }

    displayPerfumes(filteredData, gender) {
        filteredData = Array.isArray(filteredData) ? filteredData : [filteredData];
        gender = gender || '';
        this.allPerfumes = filteredData;
        this.activeType  = 'perfumes';
        this.currentPage = 1;
        const label = gender === 'unisex'
            ? _ct('unisex_perfumes_label', 'Unisex Perfumes')
            : gender === 'men'
                ? _ct('men_perfumes_label', "Men's Perfumes")
                : gender === 'women'
                    ? _ct('women_perfumes_label', "Women's Perfumes")
                    : _ct('perfumes_label', 'Perfumes');
        this._updateListHeader(label, filteredData.length);
        this._renderPage();
        document.getElementById('catListEmpty').style.display   = 'none';
        document.getElementById('catListContent').style.display = 'flex';
        this._collapseSidebar();
    }

    _updateListHeader(title, count) {
        const hdr = document.getElementById('clcHeader');
        if (!hdr) return;
        var self = this;
        hdr.innerHTML =
            '<div class="clc-header-top">' +
                '<button class="cat-categories-btn" id="catCategoriesBtn">&#9776; ' + _ct('categories', 'Categories') + '</button>' +
                '<div class="clc-label">' + _ct('catalogue_header', 'CATALOGUE') + '</div>' +
            '</div>' +
            '<div class="clc-title">' + title + '</div>' +
            '<div class="clc-count">' + count + ' ' + _ct('results', 'results') + '</div>';
        var btn = document.getElementById('catCategoriesBtn');
        if (btn) btn.addEventListener('click', function() { self._toggleSidebar(); });
    }

    // ── List item elements ─────────────────────────────────────────────────

    createPerfumeElement(perfume, index) {
        if (!perfume) return document.createElement('div');

        const el     = document.createElement('div');
        el.className = 'perfume-item';
        el.setAttribute('data-product', perfume.product);

        const col      = this._colourForItem(perfume);
        const letter   = (perfume.product || '?')[0].toUpperCase();
        const imgSrc   = perfume.image || '';
        const priceStr = this._minPerfumePrice ? _ct('from', 'from') + ' ' + this._minPerfumePrice + '€' : '—';

        const heroStyle = imgSrc
            ? 'background-image:url(\'' + imgSrc + '\');background-size:cover;background-position:center;'
            : 'background:' + col + ';';

        el.innerHTML =
            '<span class="cat-card-num">' + String(index + 1).padStart(2, '0') + '</span>' +
            '<div class="cat-card-hero" style="' + heroStyle + '">' +
                (imgSrc ? '' : '<span class="cat-card-letter">' + letter + '</span>') +
                '<div class="cat-card-grad"></div>' +
            '</div>' +
            '<div class="cat-card-body">' +
                '<div class="cat-card-name">' + (perfume.product || 'Unknown') + '</div>' +
                // '<div class="cat-card-sub">' + (perfume.brand || '') + '</div>' +
                '<div class="cat-card-price-row">' +
                    '<span class="cat-card-price">' + priceStr + '</span>' +
                '</div>' +
            '</div>';

        el.addEventListener('click', function() {
            document.querySelectorAll('.perfume-item,.product-item').forEach(function(r) { r.classList.remove('active'); });
            el.classList.add('active');
            this.showPerfumeDetails(perfume);
        }.bind(this));

        return el;
    }

    createProductElement(product, index) {
        if (!product) return document.createElement('div');

        const el     = document.createElement('div');
        el.className = 'product-item';
        el.setAttribute('data-product', product.productname);
        el.setAttribute('data-category', product.category);

        const col      = this._colourForItem(product);
        const letter   = (product.productname || '?')[0].toUpperCase();
        const imgSrc   = product.image || '';
        const priceStr = product.price ? _ct('from', 'from') + ' ' + product.price + '€' : '—';

        const heroStyle = imgSrc
            ? 'background-image:url(\'' + imgSrc + '\');background-size:cover;background-position:center;'
            : 'background:' + col + ';';

        el.innerHTML =
            '<span class="cat-card-num">' + String(index + 1).padStart(2, '0') + '</span>' +
            '<div class="cat-card-hero" style="' + heroStyle + '">' +
                (imgSrc ? '' : '<span class="cat-card-letter">' + letter + '</span>') +
                '<div class="cat-card-grad"></div>' +
            '</div>' +
            '<div class="cat-card-body">' +
                '<div class="cat-card-name">' + product.productname + '</div>' +
                '<div class="cat-card-sub">' + product.category + '</div>' +
                '<div class="cat-card-price-row">' +
                    '<span class="cat-card-price">' + priceStr + '</span>' +
                '</div>' +
            '</div>';

        el.addEventListener('click', function() {
            document.querySelectorAll('.perfume-item,.product-item').forEach(function(r) { r.classList.remove('active'); });
            el.classList.add('active');
            this.showProductDetails(product);
        }.bind(this));

        return el;
    }

    // ── Detail panel helpers ───────────────────────────────────────────────

    _isMobile() { return window.innerWidth <= 768; }

    _colourForItem(item) {
        const palette = ['#7a6050', '#3a6878', '#2a1838', '#806040', '#405870', '#506040', '#685040', '#503858', '#405850', '#382850', '#7a5040', '#486058'];
        const name = item.product || item.productname || '';
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return palette[Math.abs(hash) % palette.length];
    }

    _buildSizePills(containerId, options, onSelect) {
        const grid = document.getElementById(containerId);
        if (!grid) return;
        grid.innerHTML = '';
        options.forEach(function(o, i) {
            const btn = document.createElement('button');
            btn.className        = 'size-pill' + (i === 0 ? ' sel' : '');
            btn.dataset.value    = o.value;
            btn.dataset.price    = o.price    || '';
            btn.dataset.wcat     = o.wcat     || '';
            btn.dataset.duration = o.duration || '';
            btn.innerHTML = '<span class="sp-label">' + o.label + '</span>' +
                (o.price ? '<span class="sp-price">' + o.price + '€</span>' : '');
            btn.addEventListener('click', function() {
                grid.querySelectorAll('.size-pill').forEach(function(b) { b.classList.remove('sel'); });
                btn.classList.add('sel');
                if (onSelect) onSelect(o);
            });
            grid.appendChild(btn);
        });
        if (options.length && onSelect) onSelect(options[0]);
    }

    _closeDetailPanel() {
        const content = document.getElementById('cdpContent');
        const empty   = document.getElementById('cdpEmpty');
        if (content) content.classList.remove('visible');
        if (empty)   empty.style.display = 'flex';
        document.querySelectorAll('.perfume-item,.product-item').forEach(function(r) { r.classList.remove('active'); });
        document.body.classList.remove('detail-panel-open');
        document.body.classList.remove('no-scroll');

        // Restore cdpName visibility in case it was hidden for the photo hero
        const cdpName = document.getElementById('cdpName');
        if (cdpName) cdpName.style.display = '';

        if (this.threeController) this.threeController.removeCurrentPerfumeBottle();
    }

    _openPanel(item, notesHtml, sizeOptions, optLabel, onAddToCart, isPerfume) {
        if (this._isMobile()) {
            this._openMobileSheet(item, notesHtml, sizeOptions, optLabel, onAddToCart, isPerfume);
        } else {
            this._openDesktopPanel(item, notesHtml, sizeOptions, optLabel, onAddToCart, isPerfume);
        }
        // Bottle removed — no Three.js call here
    }

    // ── DESKTOP PANEL ─────────────────────────────────────────────────────
    //
    // Hero: owner crafting photo fills cdpHeroBg.
    // A cream label card is injected centred in cdpHero so the perfume name
    // appears "stamped" on the bottle the owner is holding.
    //
    // The photo is always the same static file — only the label text changes
    // per perfume. After the first click the browser caches the photo,
    // so every subsequent click costs zero extra network.
    //
    // PHOTO PATH: update './assets/owner-crafting.jpg' to match the
    // actual filename once the owner provides the image.
    // ──────────────────────────────────────────────────────────────────────
    _openDesktopPanel(item, notesHtml, sizeOptions, optLabel, onAddToCart, isPerfume) {
        const content = document.getElementById('cdpContent');
        const empty   = document.getElementById('cdpEmpty');
        const name    = item.product || item.productname || '';

        // ── Hide the original cdpName — the hero overlay shows it instead ──
        const cdpNameEl = document.getElementById('cdpName');
        if (cdpNameEl) {
            cdpNameEl.textContent = name;
            cdpNameEl.style.display = 'none';
        }

        // ── Collect note tags (used by both hero states) ──────────────────
        const rawNotes = item.notes || item.note || item.mainnotes || '';
        const noteTags = [...new Set(
            rawNotes.split(',').map(t => t.trim()).filter(Boolean)
        )].slice(0, 4);

        // ── Wire up the rest of the panel immediately (no need to wait
        //    for image detection before notes/sizes/price are ready) ────────
        document.getElementById('cdpNotes').innerHTML      = notesHtml;
        document.getElementById('cdpOptLabel').textContent = optLabel;
        document.getElementById('cdpConfirm').textContent  = '';
        document.getElementById('cdpAddTxt').textContent   = _ct('add_to_cart', 'Add to Cart');

        this._buildSizePills('cdpSizeGrid', sizeOptions, function(o) {
            const el = document.getElementById('cdpPrice');
            if (el) el.textContent = (o.price || '—') + '€';
        });

        // ── Fragrance pyramid — perfumes only, desktop only ──────────
        this._renderPyramid(isPerfume ? item : null);

        document.getElementById('cdpAddBtn').onclick = function() {
            onAddToCart(document.getElementById('cdpSizeGrid'));
            document.getElementById('cdpAddTxt').textContent = _ct('added_to_cart', 'Added ✓');
            setTimeout(function() {
                document.getElementById('cdpAddTxt').textContent  = _ct('add_to_cart', 'Add to Cart');
                document.getElementById('cdpConfirm').textContent = '';
            }, 2200);
        };

        document.getElementById('cdpClose').onclick = this._closeDetailPanel.bind(this);

        // Prev / next within current page
        const array     = this.activeType === 'perfumes' ? this.allPerfumes : this.allProducts;
        const start     = (this.currentPage - 1) * this.itemsPerPage;
        const pageItems = array.slice(start, start + this.itemsPerPage);
        const key       = item.product || item.productname;
        const itemIdx   = pageItems.findIndex(function(x) { return (x.product || x.productname) === key; });

        const prevBtn = document.getElementById('cdpPrev');
        const nextBtn = document.getElementById('cdpNext');
        prevBtn.style.opacity = itemIdx <= 0 ? '0.3' : '1';
        nextBtn.style.opacity = itemIdx >= pageItems.length - 1 ? '0.3' : '1';
        prevBtn.onclick = function() { if (itemIdx > 0) this._openPanelForItem(pageItems[itemIdx - 1]); }.bind(this);
        nextBtn.onclick = function() { if (itemIdx < pageItems.length - 1) this._openPanelForItem(pageItems[itemIdx + 1]); }.bind(this);

        if (empty)   empty.style.display = 'none';
        content.classList.add('visible');
        document.body.classList.add('detail-panel-open');

        // ── Hero: detect owner photo, apply correct visual state ──────────
        //
        // PHOTO PATH — update this string when the owner provides the image.
        // The filename/path is the only thing that ever needs changing here.
        const OWNER_PHOTO = './assets/owner-crafting.jpg';

        const probe = new Image();

        probe.onload = () => {
            // ── STATE A: Photo found → photo background + cream label card ──
            this._applyHeroPhoto(OWNER_PHOTO, name, noteTags, isPerfume);
        };

        probe.onerror = () => {
            // ── STATE B: Photo missing → card colour + etched text (option C) ──
            this._applyHeroFallback(name, noteTags, this._colourForItem(item), isPerfume);
        };

        // Trigger the check — onload/onerror fires once browser resolves the src
        probe.src = OWNER_PHOTO;
    }

    // ── HERO STATE A: owner photo present ────────────────────────────────
    // Cream label card centred over the photo — name "stamped" on the bottle.
    _applyHeroPhoto(photoSrc, name, noteTags, isPerfume) {
        const heroBg = document.getElementById('cdpHeroBg');
        const hero   = document.getElementById('cdpHero');
        if (!hero) return;

        // Photo background
        if (heroBg) {
            heroBg.style.cssText =
                'background-image:url(\'' + photoSrc + '\');' +
                'background-size:cover;' +
                'background-position:center center;' +
                'position:absolute;inset:0;';
        }

        // Remove any leftover overlay from a previous state
        hero.querySelectorAll('.cdp-name-label,.cdp-hero-fallback,.cdp-hero-tag').forEach(el => el.remove());
        const label = document.createElement('div');
        label.className = 'cdp-name-label';
        label.style.cssText = [
            'position:absolute', 'left:50%', 'top:50%',
            'transform:translate(-50%,-50%)',
            'display:flex', 'flex-direction:column', 'align-items:center',
            'padding:10px 24px 13px',
            'background:rgba(242,234,215,0.88)',
            'border:0.5px solid rgba(170,150,110,0.55)',
            'border-radius:2px',
            'min-width:140px', 'max-width:55%',
            'z-index:2', 'pointer-events:none', 'box-sizing:border-box',
        ].join(';');

        label.innerHTML =
            '<div style="width:82%;height:0.5px;background:rgba(110,90,55,0.28);margin:0 auto 6px"></div>' +
            '<div style="font-family:Georgia,serif;font-style:italic;' +
                'font-size:clamp(13px,2.2vw,20px);color:rgba(35,25,10,0.88);' +
                'text-align:center;line-height:1.25;word-break:break-word">' +
                _escHtml(name) +
            '</div>' +
            '<div style="width:82%;height:0.5px;background:rgba(110,90,55,0.28);margin:6px auto 5px"></div>' +
            (isPerfume
                ? '<div style="font-size:clamp(7px,1vw,9px);letter-spacing:0.15em;' +
                    'text-transform:uppercase;color:rgba(100,80,45,0.58);text-align:center">' +
                    'Eau de Parfum</div>'
                : '');

        hero.appendChild(label);

        // Note tags in the existing cdpTags element (top of hero)
        const tagsEl = document.getElementById('cdpTags');
        if (tagsEl) {
            tagsEl.innerHTML = '';
            noteTags.forEach(function(t) {
                const s = document.createElement('span');
                s.className = 'cdp-tag'; s.textContent = t;
                tagsEl.appendChild(s);
            });
        }
    }

    // ── HERO STATE B: owner photo missing → option C fallback ────────────
    // Dark atmospheric gradient, etched name with deep text-shadow,
    // horizontal rules above/below, note tags floating in the corners.
    // No card, no box — just the name as if embossed into the background.
    _applyHeroFallback(name, noteTags, colour, isPerfume) {
        const heroBg = document.getElementById('cdpHeroBg');
        const hero   = document.getElementById('cdpHero');
        if (!hero) return;

        // Card colour gradient — same palette as the catalogue cards and mobile hero
        const col = colour || '#3a4535';
        if (heroBg) {
            heroBg.style.cssText =
                'background:linear-gradient(135deg,' + col + ' 0%,' + col + 'dd 50%,' + col + '99 100%);' +
                'position:absolute;inset:0;';
        }

        // Remove any leftover overlay from a previous state
        hero.querySelectorAll('.cdp-name-label,.cdp-hero-fallback,.cdp-hero-tag').forEach(el => el.remove());
        const wrap = document.createElement('div');
        wrap.className = 'cdp-hero-fallback';
        wrap.style.cssText = [
            'position:absolute', 'left:50%', 'top:50%',
            'transform:translate(-50%,-50%)',
            'display:flex', 'flex-direction:column', 'align-items:center',
            'gap:6px', 'z-index:2', 'pointer-events:none',
            'width:80%', 'text-align:center',
        ].join(';');

        // Top rule
        const ruleStyle = 'display:block;width:100px;height:0.5px;background:rgba(255,255,255,0.30);margin:0 auto';

        // Main name — deep shadow so it reads on any dark background
        const nameEl = document.createElement('div');
        nameEl.style.cssText =
            'font-family:Georgia,serif;font-style:italic;' +
            'font-size:clamp(16px,2.8vw,26px);' +
            'color:rgba(245,235,215,0.96);' +
            'line-height:1.2;word-break:break-word;' +
            'text-shadow:0 0 24px rgba(0,0,0,0.9),0 2px 6px rgba(0,0,0,0.95);';
        nameEl.textContent = name;

        // Sub-label
        const subEl = document.createElement('div');
        subEl.style.cssText =
            'font-size:clamp(8px,1.1vw,10px);letter-spacing:0.18em;' +
            'text-transform:uppercase;color:rgba(210,195,165,0.60);' +
            'text-shadow:0 1px 4px rgba(0,0,0,0.8);';
        subEl.textContent = 'Eau de Parfum';
        subEl.style.display = isPerfume ? '' : 'none';

        wrap.innerHTML = '<span style="' + ruleStyle + '"></span>';
        wrap.appendChild(nameEl);
        wrap.innerHTML +=
            '<span style="' + ruleStyle + '"></span>';
        wrap.appendChild(subEl);
        hero.appendChild(wrap);

        // Floating note tags in corners — classed so cleanup works on next perfume open
        const positions = [
            { top:'10%',    left:'3%'  },
            { top:'10%',    right:'3%' },
            { bottom:'12%', left:'3%'  },
            { bottom:'12%', right:'3%' },
        ];
        noteTags.forEach(function(tag, i) {
            const pos = positions[i];
            const t   = document.createElement('span');
            t.className = 'cdp-hero-tag';   // ← class enables cleanup on next open
            let ps    = 'position:absolute;';
            if (pos.top)    ps += 'top:'    + pos.top    + ';';
            if (pos.bottom) ps += 'bottom:' + pos.bottom + ';';
            if (pos.left)   ps += 'left:'   + pos.left   + ';';
            if (pos.right)  ps += 'right:'  + pos.right  + ';';
            t.style.cssText = ps +
                'font-size:clamp(9px,1vw,16px);padding:4px 11px;border-radius:999px;' +
                'background:rgba(255,255,255,0.08);border:0.5px solid rgba(255,255,255,0.20);' +
                'color:rgba(235,225,205,0.75);pointer-events:none;white-space:nowrap;' +
                'text-shadow:0 1px 3px rgba(0,0,0,0.6);';
            t.textContent = tag;
            hero.appendChild(t);
        });

        // Clear the cdpTags bar — tags are in the hero itself in this state
        const tagsEl = document.getElementById('cdpTags');
        if (tagsEl) tagsEl.innerHTML = '';
    }

    // ── Fragrance Pyramid renderer ───────────────────────────────────────────
    // Called with the perfume item for perfumes, null for all other products.
    // Builds the three-tier pyramid from topnotes / middlenotes / base fields.
    // Desktop only — the section is hidden on mobile via CSS.
    _renderPyramid(item) {
        const section = document.getElementById('cdpPyramidSection');
        const container = document.getElementById('cdpPyramid');
        if (!section || !container) return;

        // Hide for non-perfume products
        if (!item) {
            section.style.display = 'none';
            container.innerHTML   = '';
            return;
        }

        const tiers = [
            { label: _ct('pyr_top',    'Top Notes'),    value: item.topnotes,    cls: 'cdp-pyr-top'  },
            { label: _ct('pyr_middle', 'Middle Notes'), value: item.middlenotes, cls: 'cdp-pyr-mid'  },
            { label: _ct('pyr_base',   'Base Notes'),   value: item.base,        cls: 'cdp-pyr-base' },
        ];

        // Only show if at least one tier has data
        const hasData = tiers.some(t => t.value && t.value.trim());
        if (!hasData) {
            section.style.display = 'none';
            container.innerHTML   = '';
            return;
        }

        // Update pyramid section title with correct language
        const pyrTitle = section.querySelector('.cdp-label');
        if (pyrTitle) pyrTitle.textContent = _ct('fragrance_pyramid', 'Fragrance Pyramid');

        container.innerHTML = '';
        const frag = document.createDocumentFragment();

        tiers.forEach((tier, i) => {
            if (!tier.value || !tier.value.trim()) return;

            // Row wrapper
            const row = document.createElement('div');
            row.className = 'cdp-pyr-row ' + tier.cls;

            // Tier label
            const lbl = document.createElement('div');
            lbl.className   = 'cdp-pyr-label';
            lbl.textContent = tier.label;
            row.appendChild(lbl);

            // Note tags
            const bar = document.createElement('div');
            bar.className = 'cdp-pyr-bar';
            tier.value.split(',').map(n => n.trim()).filter(Boolean).forEach(note => {
                const tag = document.createElement('span');
                tag.className   = 'cdp-pyr-tag';
                tag.textContent = note;
                bar.appendChild(tag);
            });
            row.appendChild(bar);
            frag.appendChild(row);

            // Divider line between tiers (not after last)
            if (i < tiers.length - 1) {
                const line = document.createElement('div');
                line.className = 'cdp-pyr-line';
                frag.appendChild(line);
            }
        });

        container.appendChild(frag);
        section.style.display = '';
    }

    // ── MOBILE SHEET ──────────────────────────────────────────────────────
    //
    // Hero: gradient from _colourForItem() — same palette as catalogue cards.
    // Floating note-tag pills are injected around the centred perfume name.
    // No image is loaded on mobile — zero network cost for the photo.
    // ──────────────────────────────────────────────────────────────────────
    _openMobileSheet(item, notesHtml, sizeOptions, optLabel, onAddToCart, isPerfume) {
        const sheet    = document.getElementById('catSheet');
        const backdrop = document.getElementById('catSheetBackdrop');
        const name     = item.product || item.productname || '';
        const col      = this._colourForItem(item);

        // ── Hero: product photo (cached) or colour gradient fallback ─────────
        const heroBg = document.getElementById('cssHeroBg');
        if (heroBg) {
            const imgSrc = !isPerfume && item.image ? item.image : '';
            if (imgSrc) {
                // Same URL the card already loaded — browser serves from cache,
                // zero extra network cost.
                heroBg.style.background = '';
                heroBg.style.backgroundImage    = 'url(\'' + imgSrc + '\')';
                heroBg.style.backgroundSize     = 'cover';
                heroBg.style.backgroundPosition = 'center';
                // Dark overlay so white product name stays readable
                heroBg.style.boxShadow = 'inset 0 0 0 9999px rgba(0,0,0,0.38)';
            } else {
                // Perfumes or products without a photo: keep colour gradient
                heroBg.style.backgroundImage    = '';
                heroBg.style.backgroundSize     = '';
                heroBg.style.backgroundPosition = '';
                heroBg.style.boxShadow          = '';
                heroBg.style.background = 'linear-gradient(135deg,' + col + ',' + col + 'bb)';
            }
        }

        // ── Hero: inject floating note tags ───────────────────────────────
        this._injectFloatingTagStyles();
        const hero = document.getElementById('cssHero');
        if (hero) {
            // Clear previously injected tags and centred name
            hero.querySelectorAll('.css-float-tag,.css-hero-name').forEach(function(el) { el.remove(); });

            // ── Centred name block inside the hero ────────────────────────
            const nameWrap = document.createElement('div');
            nameWrap.className = 'css-hero-name';
            nameWrap.style.cssText = [
                'position:absolute', 'left:50%', 'top:50%',
                'transform:translate(-50%,-50%)',
                'display:flex', 'flex-direction:column', 'align-items:center',
                'text-align:center', 'z-index:3', 'pointer-events:none',
                'width:65%',
            ].join(';');

            const heroNameEl = document.createElement('div');
            heroNameEl.style.cssText =
                'font-family:\'EB Garamond\',Georgia,serif;font-style:italic;' +
                'font-size:clamp(22px,7vw,34px);' +
                'color:rgba(255,255,255,0.97);line-height:1.15;' +
                'text-shadow:0 1px 12px rgba(0,0,0,0.35);word-break:break-word;';
            heroNameEl.textContent = name;

            const heroSubEl = document.createElement('div');
            heroSubEl.style.cssText =
                'font-size:10px;letter-spacing:0.14em;text-transform:uppercase;' +
                'color:rgba(255,255,255,0.50);margin-top:5px;';
            heroSubEl.textContent = 'Eau de Parfum';
            heroSubEl.style.display = isPerfume ? '' : 'none';

            nameWrap.appendChild(heroNameEl);
            nameWrap.appendChild(heroSubEl);
            hero.appendChild(nameWrap);

            // Collect up to 4 note tags from whatever fields are populated
            const raw  = [item.notes, item.note, item.mainnotes].filter(Boolean).join(',');
            const tags = [...new Set(
                raw.split(',').map(function(t) { return t.trim(); }).filter(Boolean)
            )].slice(0, 4);

            // Four corner positions — kept away from centre name area
            const positions = [
                { top: '8%',    left: '3%',   animDelay: '0s'   },
                { top: '8%',    right: '3%',  animDelay: '1.5s' },
                { bottom: '10%', left: '3%',  animDelay: '2.4s' },
                { bottom: '10%', right: '3%', animDelay: '0.8s' },
            ];

            tags.forEach(function(tag, i) {
                const pos = positions[i] || positions[i % positions.length];
                const el  = document.createElement('span');
                el.className = 'css-float-tag';
                el.textContent = tag;

                let posStyle = 'position:absolute;';
                if (pos.top)    posStyle += 'top:'    + pos.top    + ';';
                if (pos.bottom) posStyle += 'bottom:' + pos.bottom + ';';
                if (pos.left)   posStyle += 'left:'   + pos.left   + ';';
                if (pos.right)  posStyle += 'right:'  + pos.right  + ';';
                posStyle += 'animation-delay:' + pos.animDelay + ';';

                el.style.cssText = posStyle;
                hero.appendChild(el);
            });
        }

        // ── Name and rest of sheet ────────────────────────────────────────
        document.getElementById('cssName').textContent     = name;
        document.getElementById('cssNotes').innerHTML      = notesHtml;
        document.getElementById('cssOptLabel').textContent = optLabel;
        document.getElementById('cssConfirm').textContent  = '';
        document.getElementById('cssAddTxt').textContent   = _ct('add_to_cart', 'Add to Cart');

        this._buildSizePills('cssSizeGrid', sizeOptions, null);

        document.getElementById('cssAddBtn').onclick = function() {
            onAddToCart(document.getElementById('cssSizeGrid'));
            document.getElementById('cssAddTxt').textContent  = _ct('added_to_cart', 'Added ✓');
            setTimeout(function() {
                document.getElementById('cssAddTxt').textContent  = _ct('add_to_cart', 'Add to Cart');
                document.getElementById('cssConfirm').textContent = '';
            }, 2200);
        };

        const self  = this;
        const close = function() {
            backdrop.classList.remove('open');
            sheet.classList.remove('open');
            document.body.classList.remove('no-scroll');
            document.body.classList.remove('detail-panel-open');
            if (self.threeController) {
                self.threeController.removeCurrentPerfumeBottle(); // no-op, kept for safety
            }
        };
        document.getElementById('cssClose').onclick = close;
        backdrop.onclick = close;

        backdrop.classList.add('open');
        sheet.classList.add('open');
        document.body.classList.add('no-scroll');
        document.body.classList.add('detail-panel-open');
    }

    // ── One-time style injection for mobile floating tags ─────────────────
    // Called before first mobile sheet open. Skips if already injected.
    _injectFloatingTagStyles() {
        if (document.getElementById('css-float-tag-styles')) return;

        const style = document.createElement('style');
        style.id    = 'css-float-tag-styles';
        style.textContent = `
            @keyframes cssTagFloat {
                0%, 100% { transform: translateY(0px); }
                50%       { transform: translateY(-8px); }
            }
            .css-float-tag {
                border-radius: 999px;
                font-size: 14px;
                font-weight: 400;
                padding: 7px 16px;
                border: 1.5px solid rgba(255,255,255,0.32);
                background: rgba(255,255,255,0.14);
                color: rgba(255,255,255,0.93);
                white-space: nowrap;
                pointer-events: none;
                animation: cssTagFloat 5s ease-in-out infinite;
                z-index: 2;
            }
        `;
        document.head.appendChild(style);
    }

    _openPanelForItem(item) {
        document.querySelectorAll('.perfume-item,.product-item').forEach(function(r) { r.classList.remove('active'); });
        const key = item.product || item.productname;
        const el  = document.querySelector('[data-product="' + key + '"]');
        if (el) el.classList.add('active');
        if (this.activeType === 'perfumes') this.showPerfumeDetails(item);
        else this.showProductDetails(item);
    }

    // ── Show Perfume Details ───────────────────────────────────────────────
    //
    // Prices are cached after the first fetch — every subsequent perfume
    // click reuses the cached data. get_prices.php?category=Perfumes returns
    // the same rows regardless of which perfume is selected.
    // ──────────────────────────────────────────────────────────────────────
    showPerfumeDetails(perfume) {
        const noteFields = [
            [_ct('year',           'Year'),           perfume.year],
            [_ct('notes',          'Notes'),          perfume.notes],
            [_ct('note',           'Note'),           perfume.note],
            [_ct('middle_notes',   'Middle Notes'),   perfume.middlenotes],
            [_ct('main_notes',     'Main Notes'),     perfume.mainnotes],
            [_ct('top_notes',      'Top Notes'),      perfume.topnotes],
            [_ct('aromatic_notes', 'Aromatic Notes'), perfume.aromaticnotes],
            [_ct('base_notes',     'Base'),           perfume.base],
            [_ct('contains',       'Contains'),       perfume.contains],
        ];
        const notesHtml = noteFields.filter(function(x) { return x[1]; })
            .map(function(x) { return '<strong>' + x[0] + ':</strong> ' + x[1]; }).join('<br>');

        const self = this;

        const proceed = function(pricesData) {
            const options = pricesData.map(function(p) {
                return {
                    label: p.weight <= 0.014 + Number.EPSILON ? p.weightcategory + ' (Pure)' : p.weightcategory,
                    value: p.weight,
                    price: p.price,
                    wcat:  p.weightcategory
                };
            });

            const onAddToCart = function(grid) {
                const sel = grid.querySelector('.size-pill.sel');
                if (!sel) { alert('Please choose a size.'); return; }
                self.cartController.addToCart(perfume, sel.dataset.price, sel.dataset.value, null, null, null, perfume.brand, sel.dataset.wcat);
            };

            self._openPanel(perfume, notesHtml, options, _ct('choose_size', 'Choose Size'), onAddToCart, true);
        };

        // Use cache if available — avoids a network round-trip on every click
        if (this._priceCache) {
            proceed(this._priceCache);
            return;
        }

        fetch('./get_prices.php?category=Perfumes')
            .then(function(r) { return r.json(); })
            .then(function(pricesData) {
                self._priceCache = pricesData; // cache for all future clicks
                proceed(pricesData);
            })
            .catch(function(err) { console.error('Error loading perfume prices:', err); });
    }

    // ── Show Product Details ───────────────────────────────────────────────

    showProductDetails(product) {
        if (this.threeController && this.threeController.removeCurrentPerfumeBottle) {
            this.threeController.removeCurrentPerfumeBottle();
        }

        const descText  = this.getProductDescription(product);
        const notesHtml = descText ? '<p>' + descText + '</p>' : '<p>Discover our ' + product.category + '.</p>';
        const self      = this;

        fetch('./get_perfumes.php')
            .then(function(r) { return r.json(); })
            .then(function(perfumesData) {
                self.perfumesData = perfumesData;
                return fetch('./get_prices.php?category=' + product.category)
                    .then(function(r) { return r.json(); })
                    .then(function(pricesData) {
                        var options    = [];
                        var optLabel   = _ct('options', 'Options');
                        var optionType = 'price';

                        switch (product.category) {
                            case 'Aromatic Candles':
                                optLabel   = _ct('choose_duration', 'Choose Duration');
                                optionType = 'duration';
                                options    = pricesData.map(function(p) {
                                    return {
                                        label:    p.duration + 'h',
                                        value:    p.weight,
                                        price:    p.price,
                                        duration: p.duration
                                    };
                                });
                                break;

                            case 'Colognes':
                                optLabel   = _ct('choose_size', 'Choose Size');
                                optionType = 'size';
                                options    = pricesData.map(function(p) {
                                    return {
                                        label: p.weight + 'kg',
                                        value: p.weight,
                                        price: p.price
                                    };
                                });
                                break;

                            case 'Customised Body Care Products': {
                                var isToothpaste =
                                    product.productname.toLowerCase().includes('toothpaste') ||
                                    product.productname.toLowerCase().includes('οδοντοκρεμα');

                                if (!isToothpaste) {
                                    optLabel   = _ct('choose_scent', 'Choose Scent');
                                    optionType = 'scent';
                                    options    = perfumesData.map(function(p) {
                                        return {
                                            label: p.product,
                                            value: p.product,
                                            price: product.price
                                        };
                                    });
                                } else {
                                    optLabel   = _ct('price', 'Price');
                                    optionType = 'price';
                                    options    = [{
                                        label: product.weight + 'kg',
                                        value: product.weight,
                                        price: product.price
                                    }];
                                }
                                break;
                            }

                            case 'Auto Freshers':
                            case 'Interior Freshers':
                                if (product.productname.includes('with Perfume')) {
                                    optLabel   = _ct('choose_scent', 'Choose Scent');
                                    optionType = 'scent';
                                    options    = perfumesData.map(function(p) {
                                        return {
                                            label: p.product,
                                            value: p.product,
                                            price: product.price
                                        };
                                    });
                                } else if (product.productname.includes('with Fragrance')) {
                                    optLabel   = _ct('choose_fragrance', 'Choose Fragrance');
                                    optionType = 'fragrance';
                                    options    = fragrance.map(function(p) {
                                        return {
                                            label: p.Name,
                                            value: p.Name,
                                            price: product.price
                                        };
                                    });
                                } else {
                                    optLabel   = _ct('price', 'Price');
                                    optionType = 'price';
                                    options    = [{
                                        label: product.weight + 'kg',
                                        value: product.weight,
                                        price: product.price
                                    }];
                                }
                                break;

                            default:
                                optLabel   = _ct('price', 'Price');
                                optionType = 'price';
                                options    = [{
                                    label: product.weight + 'kg',
                                    value: product.weight,
                                    price: product.price
                                }];
                        }

                        var onAddToCart = function(grid) {
                            var sel = grid.querySelector('.size-pill.sel');

                            var w   = product.weight;
                            var p   = product.price;
                            var sc  = null;
                            var fr  = null;
                            var dur = null;

                            if (sel) {
                                p = sel.dataset.price || product.price;

                                if (optionType === 'duration' || optionType === 'size' || optionType === 'price') {
                                    w   = sel.dataset.value || product.weight;
                                    dur = sel.dataset.duration || null;
                                }

                                if (optionType === 'scent') {
                                    sc = sel.dataset.value;
                                    w  = product.weight;
                                }

                                if (optionType === 'fragrance') {
                                    fr = sel.dataset.value;
                                    w  = product.weight;
                                }
                            }

                            if (w === undefined || w === null || w === '' || Number.isNaN(Number(w))) {
                                w = product.weight;
                            }

                            self.cartController.addToCart(product, p, w, sc, fr, dur, null, null);
                        };

                        self._openPanel(product, notesHtml, options, optLabel, onAddToCart, false);
                    });
            })
            .catch(function(err) { console.error('Error loading product data:', err); });
    }

    // ── Misc ──────────────────────────────────────────────────────────────

    getCurrentLanguage() {
        const match = document.cookie.match(/(?:^|;\s*)language=([^;]*)/);
        const lang  = match ? decodeURIComponent(match[1]) : 'eng';
        return (lang === 'gr' || lang === 'eng') ? lang : 'eng';
    }

    getProductDescription(product) {
        const lang = this.getCurrentLanguage();
        return lang === 'gr'
            ? (product.description_gr || product.description_en || product.description || '')
            : (product.description_en || product.description_gr || product.description || '');
    }

    highlightSelectedPerfume(perfume) {
        const el = document.querySelector('[data-product="' + perfume + '"]');
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('nudge-animation');
        setTimeout(function() { el.classList.remove('nudge-animation'); }, 1000);
    }

    scrollToProducts() {
        if (window.innerWidth > 768 || !this.perfumesListElement) return;
        const rect = this.perfumesListElement.getBoundingClientRect();
        window.scrollTo({ top: rect.top + window.pageYOffset - 80, behavior: 'smooth' });
    }

    convertKgToMl(kg) { return kg * 1000; }

    initBackToTopButton() {
        const btn  = document.getElementById('backToTopBtn');
        const list = this.perfumesListElement;
        if (!btn || !list) return;
        const toggle = function() { btn.style.display = list.scrollTop > 20 ? 'block' : 'none'; };
        list.addEventListener('scroll', toggle);
        btn.addEventListener('click', function() { list.scrollTo({ top: 0, behavior: 'smooth' }); });
    }
}

// ── Internal helper: escape HTML in injected strings ──────────────────────
function _escHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}